import { htmlFrom } from "@common/utils";
import { defaultTo, find, isEmpty, orderBy } from "lodash";
import { DateTime } from "luxon";
import { CommunicationType, VisitSalesItemAttributesName, VisitSalesItemAttributeType } from "./Enums";
import { IVisitSalesItemAttributeListValueModel, VisitSalesItemAttributeListValueModel } from "./VisitSalesItemAttributeListValueModel";

export interface IVisitSalesItemAttributeModel {
    visitSalesItemAttributeId: number,
    visitSalesItemId: number,
    salesItemAttributeId: number,
    salesItemAttributeTypeId: number,
    name: string,
    allowMultiple: boolean | undefined,
    isReadOnly: boolean | undefined,
    sortOrder: number,
    stringValue: string,
    bitValue: boolean | undefined,
    salesItemValidationFormat: string | undefined,
    visitSalesItemAttributeListValues: IVisitSalesItemAttributeListValueModel[],
    attributeHTML: string,
}

export class VisitSalesItemAttributeModel implements Record<keyof IVisitSalesItemAttributeModel, any> {
    visitSalesItemAttributeId: number;
    visitSalesItemId: number;
    salesItemAttributeId: number;
    salesItemAttributeTypeId: number;
    name: string;
    allowMultiple: boolean;
    isReadOnly: boolean;
    sortOrder: number;
    stringValue: string;
    bitValue: boolean | undefined;
    salesItemValidationFormat: string;
    visitSalesItemAttributeListValues: VisitSalesItemAttributeListValueModel[];
    attributeHTML: string;
    selectedRabiesVaccineId: string | undefined;

    constructor(params: Partial<IVisitSalesItemAttributeModel>, visitDate?: DateTime | undefined, quantity?: number, unitOfMeasureName?: string | undefined, salesItemCommunicationTypeId?: number) {
        this.visitSalesItemAttributeId = defaultTo(params.visitSalesItemAttributeId, 0)
        this.visitSalesItemId = defaultTo(params.visitSalesItemId, 0)
        this.salesItemAttributeId = defaultTo(params.salesItemAttributeId, 0)
        this.salesItemAttributeTypeId = defaultTo(params.salesItemAttributeTypeId, 0)
        this.name = defaultTo(params.name, "")
        this.allowMultiple = defaultTo(params.allowMultiple, false)
        this.isReadOnly = defaultTo(params.isReadOnly, false)
        this.sortOrder = defaultTo(params.sortOrder, 0)
        this.stringValue = handleStringValue(params.stringValue, this.name, this.salesItemAttributeTypeId, visitDate, quantity, unitOfMeasureName, salesItemCommunicationTypeId)
        this.bitValue = params.bitValue
        this.salesItemValidationFormat = defaultTo(params.salesItemValidationFormat, "")
        this.attributeHTML = defaultTo(params.attributeHTML, "")
        const listValues = (params.visitSalesItemAttributeListValues ?? []).map(x => new VisitSalesItemAttributeListValueModel(x))
        this.visitSalesItemAttributeListValues = params.salesItemAttributeTypeId == 5 // rabies
            // sort rabies vaccines by non-expired first, then the expiration date.
            ? orderBy(listValues, [(val) => val.storageParsed["expirationDate"] && DateTime.fromFormat(val.storageParsed["expirationDate"], "M/d/yyyy") < DateTime.now(), "sortOrder"])
            // sort all others just by their sort order.
            : orderBy(listValues, ["sortOrder"])
        this.selectedRabiesVaccineId = find(this.visitSalesItemAttributeListValues, { isSelected: true })?.visitSalesItemAttributeListValueId.toString();
    }

    static parseAttributeElements = (attributes: VisitSalesItemAttributeModel[]) =>
        attributes
            .filter(a => !isEmpty(a.attributeHTML))
            .filter(a => !a.attributeHTML.includes("<span class='attributes-desc'></span>")) // the attribute isn't "empty".
            .map(a => htmlFrom(a.attributeHTML))
}


/**
 * 
 * @param stringValue string value of attribute
 * @param name key name of attribute - familiar form of a label
 * @param salesItemAttributeTypeId what form the attribute takes in a visit
 * @param visitDate date of the visit
 * @param quantity selected quantity chosen for dosage
 * @param unitOfMeasureName the {string} name of measurement (chews, pills, etc)
 * @param salesItemCommunicationTypeId designated communication type of the sales item
 * @returns 
 */
const handleStringValue = (stringValue: string | undefined, name: string, salesItemAttributeTypeId: number, visitDate: DateTime | undefined, quantity: number | undefined, unitOfMeasureName: string | undefined, salesItemCommunicationTypeId: number | undefined) => {
    if (stringValue) {
        return stringValue; // If there is already a value, return it
    }

    if (salesItemCommunicationTypeId === CommunicationType.Prescription && salesItemAttributeTypeId === VisitSalesItemAttributeType.DataEntry) {
        if (name === VisitSalesItemAttributesName.ExpiresBy) {
            if (visitDate)
                return visitDate.plus({ year: 1 }).toFormat("MM/dd/yyyy");
            else { // defensive, if visitDate is undefined default to one year from todays date.
                return DateTime.fromJSDate(new Date()).plus({ year: 1 }).toFormat("MM/dd/yyyy");
            }
        }
        else if (name === VisitSalesItemAttributesName.Quantity && quantity) {
            return quantity.toString();
        }
        else if (name === VisitSalesItemAttributesName.UnitOfMeasure && unitOfMeasureName) {
            return unitOfMeasureName;
        }
        else {
            return defaultTo(stringValue, "")
        }
    }

    else {
        return defaultTo(stringValue, "")
    }
}
